import type Store from '@ember-data/store';
import { on } from '@ember/modifier';
import type Owner from '@ember/owner';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { task } from 'ember-concurrency';
import type DmpRetrievalContext from 'parog-web/contexts/dmp-retrieval-context';
import ValidIdentityDocumentItems from 'parog-web/components/valid-identity-document-items';
import { loadValidIdentityDocuments } from 'parog-web/utils/load-valid-identity-documents';
import Button from 'ui-kit/components/button';
import Checkbox from 'ui-kit/components/checkbox';
import Dropdown from 'ui-kit/components/dropdown';

interface Args {
  context: DmpRetrievalContext;
  onDismiss: () => void;
}

export default class CnamServicesAccessFormInsDocumentComponent extends Component<Args> {
  @service declare store: Store;

  @tracked shouldShowDocumentSelection = false;

  constructor(owner: Owner, args: Args) {
    super(owner, args);

    if (!args.context.insDocumentIsQualifying) {
      this.shouldShowDocumentSelection = true;
      void this.loadValidIdentityDocumentsTask.perform();
    }
  }

  /** The modal is not a route: this step loads the list it shows. */
  loadValidIdentityDocumentsTask = task(async () => {
    await loadValidIdentityDocuments(this.store, [
      this.args.context.patient.healthCentreConfigurator
    ]);
  });

  mutInsDocumentIsQualifying = (value: string | boolean | string[]) => {
    this.args.context.insDocumentIsQualifying = value as boolean;
  };

  mutInsIdentityDocument = (value: string | string[]) => {
    this.args.context.insIdentityDocument = value as string;
  };

  onSubmit = async (event: Event): Promise<void> => {
    event.preventDefault();
    await this.args.context.transitionTo();
  };

  <template>
    <div data-test-ins-documents-state>
      <form {{on 'submit' this.onSubmit}} class='ui form'>
        <p>
          Afin de récupérer l'historique de remboursement, l'INS du patient doit
          être qualifié.<br />
          Cliquez sur continuer pour tenter la récupération d'un INS qualifié
          pour
          {{@context.patient.fullName}}.
        </p>

        {{#if this.shouldShowDocumentSelection}}
          {{#unless this.loadValidIdentityDocumentsTask.isRunning}}
            <p data-test-ins-document-to-select>
              Un document d'identité valide est nécessaire pour tenter une
              qualification.
              <br />
              Merci d'indiquer si le document d'identité est valide, ainsi que
              son type.
              <br />
            </p>
            <div class='field'>
              <Dropdown
                @labelName="Document d'identité"
                @selected={{@context.insIdentityDocument}}
                @onChange={{this.mutInsIdentityDocument}}
                as |dropdown|
              >
                <ValidIdentityDocumentItems
                  @dropdown={{dropdown}}
                  @validIdentityDocuments={{@context.patient.healthCentreConfigurator.validIdentityDocuments}}
                  @recordedCode={{@context.patient.insIdentityDocument}}
                />
              </Dropdown>
            </div>
            <div>
              <Checkbox
                @labelName="Le document d'identité est-il valide ?"
                @dataTestInput='insDocumentIsQualifying'
                @checked={{@context.insDocumentIsQualifying}}
                @onChange={{this.mutInsDocumentIsQualifying}}
              />
            </div>
          {{/unless}}
        {{/if}}

        <div class='form-buttons'>
          <Button
            type='button'
            class='ui fluid form-button'
            {{on 'click' @onDismiss}}
          >
            Annuler
          </Button>
          <Button
            data-test-button='submit-ins-access-form-documents'
            type='submit'
            class='ui fluid primary form-button
              {{unless @context.insDocumentIsQualifying "disabled"}}'
          >
            Continuer
          </Button>
        </div>
      </form>
    </div>
  </template>
}
